import { useEffect, useMemo, useState } from 'react';
import type { ReactElement } from 'react';
import { AreaChart, Area, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Layout } from '../components/Layout';
import { Markdown } from '../components/Markdown';
import { Orb } from '../components/Orb';
import { api } from '../lib/api';
import { spendShares } from '../lib/spendShare';
import { severityLabel, severityTip, incidentStatusLabel, incidentStatusTip } from '../lib/incidentCopy';
import type { AgentId, Incident, Severity, Standup } from '@shared/types';

/* GET /reports/costs response — mirrors the Worker's inline CostsReport shape. */
interface CostsReport {
  by_day: Array<{ date: string; usd: number }>;
  by_provider: Array<{ provider: string; usd: number }>;
  by_agent: Array<{ agent_id: AgentId | null; usd: number }>;
  total: number;
}

const RANGES = [7, 30, 90] as const;

// --- formatting helpers ------------------------------------------------------
const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
const usd = (n: number | null | undefined) =>
  '$' + (n ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDay = (d: string) => {
  const dt = new Date(d + 'T00:00:00');
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};
const fmtDateLong = (d: string) => {
  const dt = new Date(d + 'T00:00:00');
  return isNaN(dt.getTime()) ? d : dt.toLocaleDateString('en-US', { weekday: 'short', month: 'long', day: 'numeric' });
};
const fmtTs = (iso: string) => {
  const dt = new Date(iso);
  return isNaN(dt.getTime())
    ? iso
    : dt.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const SEV: Record<Severity, { cls: string; alarm?: boolean }> = {
  low: { cls: 'chip-dim' },
  medium: { cls: 'chip-amber' },
  high: { cls: 'chip-coral' },
  critical: { cls: 'chip-coral', alarm: true },
};
const INC_STATUS: Record<Incident['status'], string> = {
  open: 'chip-amber',
  ack: 'chip-ice',
  resolved: 'chip-mint',
};

// --- theme-aware chart colors (react to the NightOps/Daylight toggle) --------
interface ThemeColors {
  ice: string;
  faint: string;
  panel: string;
  line2: string;
}
function readColors(): ThemeColors {
  const s = getComputedStyle(document.documentElement);
  const g = (k: string, fb: string) => s.getPropertyValue(k).trim() || fb;
  return {
    ice: g('--ice', '#5BD1F5'),
    faint: g('--faint', '#4A6472'),
    panel: g('--panel', '#0E1F28'),
    line2: g('--line-2', 'rgba(122,203,235,.22)'),
  };
}
function useThemeColors(): ThemeColors {
  const [c, setC] = useState<ThemeColors>(readColors);
  useEffect(() => {
    const obs = new MutationObserver(() => setC(readColors()));
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => obs.disconnect();
  }, []);
  return c;
}

// --- custom tooltip (recharts default is white; skin it to the panel) --------
function ChartTip({
  active,
  payload,
  label,
  colors,
}: {
  active?: boolean;
  payload?: Array<{ value?: number }>;
  label?: string;
  colors: ThemeColors;
}) {
  if (!active || !payload || !payload.length) return null;
  return (
    <div
      style={{
        background: colors.panel,
        border: `1px solid ${colors.line2}`,
        borderRadius: 10,
        padding: '8px 12px',
      }}
    >
      <div className="t-label" style={{ marginBottom: 4 }}>
        {label ? fmtDay(String(label)) : ''}
      </div>
      <div className="mono" style={{ fontSize: 14, color: colors.ice }}>
        {usd(payload[0]?.value ?? 0)}
      </div>
    </div>
  );
}

// --- the spend chart: one glowing series, faint grid, mono axes --------------
function SpendChart({ data }: { data: CostsReport['by_day'] }) {
  const colors = useThemeColors();
  const reduce = typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
  const lastIndex = data.length - 1;

  const renderDot = (props: { cx?: number; cy?: number; index?: number }): ReactElement => {
    const { cx, cy, index } = props;
    if (index !== lastIndex || cx == null || cy == null) return <g key={`d${index}`} />;
    return (
      <g key="endpoint">
        <circle cx={cx} cy={cy} r={9} fill={colors.ice} opacity={0.18} />
        <circle cx={cx} cy={cy} r={4} fill={colors.ice} />
        <circle cx={cx} cy={cy} r={4} fill="none" stroke={colors.ice} strokeOpacity={0.5} />
      </g>
    );
  };

  return (
    <div style={{ height: 260, marginTop: 16 }}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 12, right: 14, bottom: 4, left: -8 }}>
          <defs>
            <linearGradient id="rp-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={colors.ice} stopOpacity={0.28} />
              <stop offset="100%" stopColor={colors.ice} stopOpacity={0} />
            </linearGradient>
            <filter id="rp-glow" x="-40%" y="-40%" width="180%" height="180%">
              <feGaussianBlur stdDeviation="4" />
            </filter>
          </defs>
          <CartesianGrid stroke={colors.ice} strokeOpacity={0.07} vertical={false} />
          <XAxis
            dataKey="date"
            tickFormatter={(v: string) => fmtDay(v)}
            tick={{ fontSize: 10, fontFamily: 'var(--mono)', fill: colors.faint }}
            tickLine={false}
            axisLine={{ stroke: colors.ice, strokeOpacity: 0.12 }}
            minTickGap={26}
          />
          <YAxis
            tickFormatter={(v: number) => '$' + Math.round(v)}
            tick={{ fontSize: 10, fontFamily: 'var(--mono)', fill: colors.faint }}
            tickLine={false}
            axisLine={false}
            width={46}
          />
          <Tooltip content={<ChartTip colors={colors} />} cursor={{ stroke: colors.line2 }} />
          {/* blurred under-stroke = the glow */}
          <Area
            type="monotone"
            dataKey="usd"
            stroke={colors.ice}
            strokeWidth={6}
            strokeOpacity={0.5}
            fill="none"
            filter="url(#rp-glow)"
            dot={false}
            isAnimationActive={false}
          />
          {/* crisp 2px core + gradient area fade + emphasized endpoint */}
          <Area
            type="monotone"
            dataKey="usd"
            stroke={colors.ice}
            strokeWidth={2}
            fill="url(#rp-area)"
            dot={renderDot}
            activeDot={{ r: 4, fill: colors.ice, stroke: 'none' }}
            isAnimationActive={!reduce}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// --- empty state: an orb + one warm line ------------------------------------
function Empty({ agent, line }: { agent: AgentId; line: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '24px 4px' }}>
      <Orb agent={agent} size="md" />
      <p className="dim" style={{ fontSize: 14 }}>
        {line}
      </p>
    </div>
  );
}

export default function Reports() {
  const [days, setDays] = useState<number>(30);
  const [costs, setCosts] = useState<CostsReport | null>(null);
  const [costErr, setCostErr] = useState<string | null>(null);
  const [incidents, setIncidents] = useState<Incident[] | null>(null);
  const [incErr, setIncErr] = useState<string | null>(null);
  const [standups, setStandups] = useState<Standup[] | null>(null);
  const [suErr, setSuErr] = useState<string | null>(null);
  // The archive grows ~7 standups/day forever, so default to the latest week of
  // days and let a founder tap to see the rest — the whole page stays phone-short.
  const [showAllStandups, setShowAllStandups] = useState(false);

  // costs re-fetch when the window changes
  useEffect(() => {
    let live = true;
    setCosts(null);
    setCostErr(null);
    api
      .get<CostsReport>(`/reports/costs?days=${days}`)
      .then((r) => live && setCosts(r))
      .catch(() => live && setCostErr("Couldn't load spend."));
    return () => {
      live = false;
    };
  }, [days]);

  // incidents + standups load once
  useEffect(() => {
    let live = true;
    api
      .get<Incident[]>('/reports/incidents')
      .then((r) => live && setIncidents(r))
      .catch(() => live && setIncErr("Couldn't load incidents."));
    api
      .get<Standup[]>('/reports/standups')
      .then((r) => live && setStandups(r))
      .catch(() => live && setSuErr("Couldn't load standups."));
    return () => {
      live = false;
    };
  }, []);

  const standupGroups = useMemo<Array<[string, Standup[]]>>(() => {
    if (!standups) return [];
    const m = new Map<string, Standup[]>();
    for (const s of standups) {
      const k = (s.date || '').slice(0, 10);
      const bucket = m.get(k) ?? m.set(k, []).get(k)!;
      bucket.push(s);
    }
    return [...m.entries()]; // API returns date DESC already
  }, [standups]);

  // Show the latest week of standup days by default; a founder taps to see the rest.
  const STANDUP_DAYS_SHOWN = 7;
  const visibleStandupGroups = showAllStandups
    ? standupGroups
    : standupGroups.slice(0, STANDUP_DAYS_SHOWN);
  const hiddenStandupDays = standupGroups.length - visibleStandupGroups.length;

  const byDay = costs?.by_day ?? [];
  const last = byDay[byDay.length - 1];
  const prev = byDay[byDay.length - 2];
  const delta = last && prev ? last.usd - prev.usd : null;

  return (
    <Layout title="Reports">
      {/* ---- SPEND ---- */}
      <h2 style={{ marginBottom: 12 }}>Spend</h2>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap' }}>
          <div className="stat">
            <div className="t-label">Total · last {days} days</div>
            <div className="num">{usd(costs?.total ?? 0)}</div>
            {delta != null && last && prev && (
              <div className="small" style={{ marginTop: 6 }}>
                <span className={delta > 0 ? 'down' : 'up'}>
                  {delta > 0 ? '▲' : delta < 0 ? '▼' : '·'} <span className="mono">{usd(Math.abs(delta))}</span>
                </span>{' '}
                {fmtDay(last.date)} vs {fmtDay(prev.date)}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }} role="group" aria-label="Time window">
            {RANGES.map((d) => (
              <button
                key={d}
                className={`btn sm ${d === days ? 'btn-line' : 'btn-ghost'}`}
                onClick={() => setDays(d)}
              >
                <span className="mono">{d}</span>d
              </button>
            ))}
          </div>
        </div>

        {costErr ? (
          <p className="small" style={{ color: 'var(--coral)', marginTop: 16 }}>
            {costErr}
          </p>
        ) : costs == null ? (
          <p className="small" style={{ marginTop: 16 }}>
            Loading…
          </p>
        ) : byDay.length === 0 ? (
          <Empty agent="emma" line="No spend logged in this window — the meter's quiet." />
        ) : (
          <SpendChart data={byDay} />
        )}
      </div>

      {/* ---- by provider / by agent ---- */}
      {costs && (costs.by_provider.length > 0 || costs.by_agent.length > 0) && (
        <div className="grid g2" style={{ marginTop: 16 }}>
          <div className="card">
            <div className="t-label" style={{ marginBottom: 12 }}>
              By provider
            </div>
            <div style={{ overflowX: 'auto' }}>
              <table className="ledger">
                <thead>
                  <tr>
                    <th>Provider</th>
                    <th className="r">Spend</th>
                  </tr>
                </thead>
                <tbody>
                  {costs.by_provider.map((r) => (
                    <tr key={r.provider}>
                      <td>{cap(r.provider)}</td>
                      <td className="r">{usd(r.usd)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="t-label" style={{ marginBottom: 12 }}>
              By agent
            </div>
            {spendShares(costs.by_agent, costs.total).map((r) => (
              <div className="spendrow" key={r.agent_id ?? 'unattributed'}>
                {r.agent_id ? <Orb agent={r.agent_id} size="xs" /> : <span className="orb-blank" />}
                <div className="spendrow-main">
                  <div className="spendrow-top">
                    <span className="spendrow-name">{r.agent_id ? cap(r.agent_id) : 'Unattributed'}</span>
                    <span className="spendrow-usd mono">{usd(r.usd)}</span>
                  </div>
                  <div
                    className={`spendbar${r.agent_id ? ` tint ${r.agent_id}` : ''}`}
                    role="img"
                    aria-label={`${r.pct} of spend`}
                  >
                    <span style={{ width: `${Math.round(r.share * 100)}%` }} />
                  </div>
                </div>
                <span className="spendrow-pct mono">{r.pct}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- INCIDENTS ---- */}
      <h2 style={{ margin: '30px 0 12px' }}>Incidents</h2>
      <div className="card">
        {incErr ? (
          <p className="small" style={{ color: 'var(--coral)' }}>
            {incErr}
          </p>
        ) : incidents == null ? (
          <p className="small">Loading…</p>
        ) : incidents.length === 0 ? (
          <Empty agent="quinn" line="No incidents on record. Quinn's sleeping soundly." />
        ) : (
          incidents.map((it) => (
            <div className="feed-item" key={it.id}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span
                    className={`chip ${SEV[it.severity].cls}${SEV[it.severity].alarm ? ' alarm' : ''}`}
                    title={severityTip(it.severity)}
                  >
                    <i />
                    {severityLabel(it.severity)}
                  </span>
                  <span className="who">{it.title}</span>
                  <span className={`chip ${INC_STATUS[it.status]}`} title={incidentStatusTip(it.status)}>
                    <i />
                    {incidentStatusLabel(it.status)}
                  </span>
                  {(it.times_seen ?? 1) > 1 && (
                    <span className="chip chip-dim" title="This isn't new — it's the same issue recurring. Reported this many times.">
                      <i />
                      seen ×{it.times_seen}
                    </span>
                  )}
                </div>
                {it.evidence_md && (
                  <Markdown text={it.evidence_md} style={{ marginTop: 4, fontSize: 13.5, color: 'var(--dim)' }} />
                )}
                {it.proposed_fix_md && (
                  <Markdown
                    text={it.proposed_fix_md}
                    prefix="Proposed fix · "
                    className="dim"
                    style={{ marginTop: 4, fontSize: 13.5 }}
                  />
                )}
                <div className="receipt">
                  #{it.id} · {it.agent_id ? cap(it.agent_id) : 'system'} ·{' '}
                  {(it.times_seen ?? 1) > 1 && it.last_seen_at
                    ? `first seen ${fmtTs(it.created_at)} · last seen ${fmtTs(it.last_seen_at)}`
                    : fmtTs(it.created_at)}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ---- STANDUP ARCHIVE ---- */}
      <h2 style={{ margin: '30px 0 12px' }}>Standup archive</h2>
      {suErr ? (
        <div className="card">
          <p className="small" style={{ color: 'var(--coral)' }}>
            {suErr}
          </p>
        </div>
      ) : standups == null ? (
        <div className="card">
          <p className="small">Loading…</p>
        </div>
      ) : standupGroups.length === 0 ? (
        <div className="card">
          <Empty agent="grace" line="No standups filed yet. The team's still stretching." />
        </div>
      ) : (
        visibleStandupGroups.map(([date, list]) => (
          <div key={date} style={{ marginBottom: 20 }}>
            <div className="t-label" style={{ margin: '0 2px 10px' }}>
              {fmtDateLong(date)}
            </div>
            <div className="grid g2">
              {list.map((su) => (
                <div className="card" key={su.id}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <Orb agent={su.agent_id} size="xs" />
                    <span style={{ fontWeight: 600, fontSize: 13.5 }}>{cap(su.agent_id)}</span>
                  </div>
                  {su.today_md && (
                    <Markdown text={su.today_md} className="small" style={{ marginTop: 10 }} />
                  )}
                  {su.wins_md && (
                    <Markdown
                      text={su.wins_md}
                      prefix="Win · "
                      className="small"
                      style={{ marginTop: 6, color: 'var(--mint)' }}
                    />
                  )}
                  {su.blocked_on_founder_md && (
                    <Markdown
                      text={su.blocked_on_founder_md}
                      prefix="Blocked · "
                      className="small"
                      style={{ marginTop: 6, color: 'var(--amber)' }}
                    />
                  )}
                  <div className="receipt">{su.date}</div>
                </div>
              ))}
            </div>
          </div>
        ))
      )}
      {standups != null && standupGroups.length > STANDUP_DAYS_SHOWN && (
        <button
          className="btn sm btn-ghost"
          onClick={() => setShowAllStandups((v) => !v)}
          style={{ marginTop: 4 }}
        >
          {showAllStandups
            ? 'Show just the latest week'
            : `Show all ${standupGroups.length} days · ${hiddenStandupDays} more`}
        </button>
      )}
    </Layout>
  );
}
